import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dices } from "lucide-react";
import { toast } from "sonner";
import PasswordStrengthBar from "@/components/PasswordStrengthBar";

export default function AuthPage() {
  const { login, register } = useAuth();
  const [tab, setTab] = useState("login");

  useEffect(() => { document.title = "Tavoli da Gioco — Accedi"; }, []);

  const [loginIdentifier, setLoginIdentifier] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [regName, setRegName] = useState("");
  const [regUsername, setRegUsername] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const onLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(loginIdentifier, loginPassword);
    setBusy(false);
    if (!res.ok) toast.error(res.error);
    else toast.success("Bentornato!");
  };

  const onRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await register(regEmail, regPassword, regName, regUsername);
    setBusy(false);
    if (!res.ok) toast.error(res.error);
    else toast.success("Registrazione completata!");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FDFBF7] dark:bg-[#181818]">
      {/* Hero */}
      <div className="relative hidden lg:block grain">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/8d0a924d-be20-4ddf-8e4f-03be94da5a1f/images/6661bdf1809908d7c75e036fee07651f3fa022f75b592108da8e714ce52aaa65.png"
          alt="Board game cafe"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#2B593F]/85 via-[#2B593F]/40 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-[#FDFBF7]">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 w-fit mb-6">
            <Dices size={14} />
            <span className="text-xs uppercase tracking-[0.18em]">Boardgame Club</span>
          </div>
          <h2 className="font-display text-4xl xl:text-5xl font-bold leading-tight max-w-md">
            Trova compagni, prenota un tavolo, gioca.
          </h2>
          <p className="mt-4 text-sm text-white/80 max-w-md leading-relaxed">
            Crea tavoli per i tuoi giochi preferiti, scegli categoria e tempi, e lascia che gli altri si uniscano alla tua serata.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-[#2B593F] flex items-center justify-center text-[#FDFBF7]">
              <Dices size={20} />
            </div>
            <h1 className="font-display text-xl font-bold text-[#2D2A26] dark:text-[#F5F1EA]">Tavoli da Gioco</h1>
          </div>

          <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#2D2A26] dark:text-[#F5F1EA] tracking-tight">
            Benvenuto al tavolo
          </h2>
          <p className="text-[#757169] dark:text-[#B8B2AA] mt-2 mb-8">
            Accedi al tuo account o registrati per iniziare.
          </p>

          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid grid-cols-2 w-full bg-[#F5F1EA] dark:bg-[#1A1A1A] rounded-xl p-1 h-auto">
              <TabsTrigger
                value="login"
                data-testid="tab-login"
                className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-[#2A2A2A] data-[state=active]:text-[#2B593F] dark:data-[state=active]:text-[#8FC79F] data-[state=active]:shadow-sm py-2.5 text-sm font-medium dark:text-[#B8B2AA]"
              >
                Accedi
              </TabsTrigger>
              <TabsTrigger
                value="register"
                data-testid="tab-register"
                className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-[#2A2A2A] data-[state=active]:text-[#2B593F] dark:data-[state=active]:text-[#8FC79F] data-[state=active]:shadow-sm py-2.5 text-sm font-medium dark:text-[#B8B2AA]"
              >
                Registrati
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-6">
              <form onSubmit={onLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email" className="text-xs uppercase tracking-[0.1em] text-[#757169] dark:text-[#B8B2AA] font-medium">Email o username</Label>
                  <Input
                    id="login-email"
                    type="text"
                    required
                    value={loginIdentifier}
                    onChange={(e) => setLoginIdentifier(e.target.value)}
                    data-testid="login-email-input"
                    className="mt-2 bg-[#FDFBF7] dark:bg-[#1A1A1A] border-[#EAE3D9] dark:border-[#2A2A2A] dark:text-[#F5F1EA] focus-visible:ring-[#2B593F]/30 focus-visible:border-[#2B593F] rounded-xl h-12"
                    placeholder="Email o username"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password" className="text-xs uppercase tracking-[0.1em] text-[#757169] dark:text-[#B8B2AA] font-medium">Password</Label>
                    <Link
                      to="/forgot-password"
                      data-testid="forgot-password-link"
                      className="text-xs text-[#C8553D] dark:text-[#F08A78] hover:underline font-medium"
                    >
                      Password dimenticata?
                    </Link>
                  </div>
                  <Input
                    id="login-password"
                    type="password"
                    required
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    data-testid="login-password-input"
                    className="mt-2 bg-[#FDFBF7] dark:bg-[#1A1A1A] border-[#EAE3D9] dark:border-[#2A2A2A] dark:text-[#F5F1EA] focus-visible:ring-[#2B593F]/30 focus-visible:border-[#2B593F] rounded-xl h-12"
                    placeholder="••••••••"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  data-testid="login-submit-button"
                  className="w-full h-12 bg-[#2B593F] hover:bg-[#1E3F2C] text-[#FDFBF7] rounded-xl font-medium"
                >
                  {busy ? "Accesso in corso..." : "Accedi"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register" className="mt-6">
              <form onSubmit={onRegister} className="space-y-4">
                <div>
                  <Label htmlFor="reg-name" className="text-xs uppercase tracking-[0.1em] text-[#757169] dark:text-[#B8B2AA] font-medium">Nome</Label>
                  <Input
                    id="reg-name"
                    type="text"
                    required
                    value={regName}
                    onChange={(e) => setRegName(e.target.value)}
                    data-testid="register-name-input"
                    className="mt-2 bg-[#FDFBF7] dark:bg-[#1A1A1A] border-[#EAE3D9] dark:border-[#2A2A2A] dark:text-[#F5F1EA] focus-visible:ring-[#2B593F]/30 focus-visible:border-[#2B593F] rounded-xl h-12"
                    placeholder="Mario Rossi"
                  />
                </div>
                <div>
                  <Label htmlFor="reg-username" className="text-xs uppercase tracking-[0.1em] text-[#757169] dark:text-[#B8B2AA] font-medium">Username (opzionale)</Label>
                  <Input
                    id="reg-username"
                    type="text"
                    value={regUsername}
                    onChange={(e) => setRegUsername(e.target.value)}
                    data-testid="register-username-input"
                    className="mt-2 bg-[#FDFBF7] dark:bg-[#1A1A1A] border-[#EAE3D9] dark:border-[#2A2A2A] dark:text-[#F5F1EA] focus-visible:ring-[#2B593F]/30 focus-visible:border-[#2B593F] rounded-xl h-12"
                    placeholder="mario_r"
                  />
                  <p className="text-[11px] text-[#757169] dark:text-[#B8B2AA] mt-1.5">Potrai accedere usando email o username.</p>
                </div>
                <div>
                  <Label htmlFor="reg-email" className="text-xs uppercase tracking-[0.1em] text-[#757169] dark:text-[#B8B2AA] font-medium">Email</Label>
                  <Input
                    id="reg-email"
                    type="email"
                    required
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    data-testid="register-email-input"
                    className="mt-2 bg-[#FDFBF7] dark:bg-[#1A1A1A] border-[#EAE3D9] dark:border-[#2A2A2A] dark:text-[#F5F1EA] focus-visible:ring-[#2B593F]/30 focus-visible:border-[#2B593F] rounded-xl h-12"
                    placeholder="tu@email.com"
                  />
                </div>
                <div>
                  <Label htmlFor="reg-password" className="text-xs uppercase tracking-[0.1em] text-[#757169] dark:text-[#B8B2AA] font-medium">Password</Label>
                  <Input
                    id="reg-password"
                    type="password"
                    required
                    minLength={8}
                    value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)}
                    data-testid="register-password-input"
                    className="mt-2 bg-[#FDFBF7] dark:bg-[#1A1A1A] border-[#EAE3D9] dark:border-[#2A2A2A] dark:text-[#F5F1EA] focus-visible:ring-[#2B593F]/30 focus-visible:border-[#2B593F] rounded-xl h-12"
                    placeholder="Almeno 8 caratteri con una lettera e un numero"
                  />
                  <PasswordStrengthBar password={regPassword} />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  data-testid="register-submit-button"
                  className="w-full h-12 bg-[#2B593F] hover:bg-[#1E3F2C] text-[#FDFBF7] rounded-xl font-medium"
                >
                  {busy ? "Creazione account..." : "Crea account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <p className="text-xs text-[#757169] dark:text-[#B8B2AA] mt-8 text-center">
            Continuando accetti di partecipare con sportività e amore per il cardboard.
          </p>
        </div>
      </div>
    </div>
  );
}
